import React from 'react';
import {
  Users,
  Video,
  UserCheck,
  CheckCircle2,
  TrendingUp,
  Award,
  UserPlus,
  Calendar,
  Send,
  PlusCircle,
  Play,
  ArrowRight,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react';
import {
  AdminStudent,
  PendingRegistration,
  ScheduledClass,
  GathaSubmission,
  AuditLogEntry,
  AdminRole,
} from '../types';
import { Badge, Button } from '../../components/core';

interface ExecutiveDashboardProps {
  students: AdminStudent[];
  registrations: PendingRegistration[];
  classes: ScheduledClass[];
  gathaSubmissions: GathaSubmission[];
  auditLogs: AuditLogEntry[];
  currentRole: AdminRole;
  onNavigateTab: (tab: string, subTab?: string) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const ExecutiveDashboard: React.FC<ExecutiveDashboardProps> = ({
  students,
  registrations,
  classes,
  gathaSubmissions,
  auditLogs,
  currentRole,
  onNavigateTab,
  addToast,
}) => {
  const pendingRegistrationsCount = registrations.filter((r) => r.status === 'Pending').length;
  const pendingGathasCount = gathaSubmissions.filter((g) => g.status === 'Pending').length;
  const activeStudentsCount = students.filter((s) => s.status === 'Active').length;
  const todayClasses = classes.filter((c) => c.dateTime.includes('2026-07-23') || c.status === 'LIVE NOW');
  const totalPointsAwarded = students.reduce((acc, curr) => acc + curr.pointsBalance, 0);

  return (
    <div className="space-y-6">
      {/* Page Title & Context Header Bar */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Executive Overview</h1>
        </div>

        {/* Quick Launch Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            leftIcon={UserPlus}
            onClick={() => onNavigateTab('people', 'students')}
          >
            + Enroll Student
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={Calendar}
            onClick={() => onNavigateTab('classes', 'live_classes')}
          >
            Schedule Class
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={Send}
            onClick={() => onNavigateTab('administration', 'broadcast')}
          >
            Broadcast Notice
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={PlusCircle}
            onClick={() => onNavigateTab('activities', 'bonus')}
          >
            Award Bonus
          </Button>
        </div>
      </div>

      {/* Enterprise KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          {
            title: 'Active Students',
            value: activeStudentsCount,
            subText: '100% Enrolled',
            icon: Users,
            badgeBg: 'bg-rose-50',
            iconColor: 'text-[var(--gv-brand)]',
            tab: 'people',
            subTab: 'students',
          },
          {
            title: "Today's Live Classes",
            value: todayClasses.length,
            subText: '1 Live Session',
            icon: Video,
            badgeBg: 'bg-indigo-50',
            iconColor: 'text-indigo-600',
            tab: 'classes',
            subTab: 'live_classes',
          },
          {
            title: 'Student Registrations',
            value: registrations.length,
            subText: 'Payment Registered',
            icon: UserCheck,
            badgeBg: 'bg-[var(--gv-brand-soft)]',
            iconColor: 'text-[var(--gv-brand)]',
            tab: 'people',
            subTab: 'approvals',
          },
          {
            title: 'Pending Gathas',
            value: pendingGathasCount,
            subText: 'Submissions Queue',
            icon: CheckCircle2,
            badgeBg: 'bg-emerald-50',
            iconColor: 'text-emerald-600',
            tab: 'content',
            subTab: 'sutras',
          },
          {
            title: 'Avg. Attendance',
            value: '91.2%',
            subText: '+2.4% vs last week',
            icon: TrendingUp,
            badgeBg: 'bg-sky-50',
            iconColor: 'text-sky-600',
            tab: 'classes',
            subTab: 'attendance',
          },
          {
            title: 'Points Balance',
            value: totalPointsAwarded,
            subText: 'Total Distributed',
            icon: Award,
            badgeBg: 'bg-purple-50',
            iconColor: 'text-purple-600',
            tab: 'activities',
            subTab: 'bonus',
          },
        ].map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <div
              key={idx}
              onClick={() => onNavigateTab(kpi.tab, kpi.subTab)}
              className="admin-card cursor-pointer p-4 transition-all hover:border-[var(--gv-brand)]/40 hover:shadow-sm group flex flex-col justify-between"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-[var(--gv-ink-muted)] uppercase tracking-wider font-mono">{kpi.title}</span>
                <div className={`w-8 h-8 rounded-lg ${kpi.badgeBg} flex items-center justify-center ${kpi.iconColor} group-hover:scale-105 transition-transform`}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-[var(--gv-ink)] tracking-tight">{kpi.value}</div>
                <p className="text-[11px] text-[var(--gv-ink-muted)] font-medium mt-0.5">{kpi.subText}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Grid: Live Classes Monitor & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Classes Monitor */}
        <div className="admin-card lg:col-span-2 space-y-4 p-5">
          <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <h3 className="text-sm font-bold text-[var(--gv-ink)]">Today's Live Classes & Schedule Monitor</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              rightIcon={ArrowRight}
              onClick={() => onNavigateTab('classes', 'live_classes')}
            >
              View Full Schedule
            </Button>
          </div>

          <div className="overflow-x-auto border border-[var(--gv-border)] rounded-lg">
            <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
              <thead className="bg-[var(--gv-surface)] text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase tracking-wider border-b border-[var(--gv-border)]">
                <tr>
                  <th className="py-2.5 px-3.5 font-bold">Class Title</th>
                  <th className="py-2.5 px-3.5 font-bold">Level / Batch</th>
                  <th className="py-2.5 px-3.5 font-bold">Faculty</th>
                  <th className="py-2.5 px-3.5 font-bold">Timing</th>
                  <th className="py-2.5 px-3.5 font-bold">Status</th>
                  <th className="py-2.5 px-3.5 font-bold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 font-medium bg-white">
                {todayClasses.map((cls) => {
                  const isLive = cls.status === 'LIVE NOW';
                  return (
                    <tr key={cls.id} className="hover:bg-[var(--gv-surface)]/80 transition-colors">
                      <td className="py-3 px-3.5 font-semibold text-[var(--gv-ink)] flex items-center gap-2">
                        <Video className="w-3.5 h-3.5 text-[var(--gv-brand)] shrink-0" />
                        <span>{cls.title}</span>
                      </td>
                      <td className="py-3 px-3.5 text-[var(--gv-ink-muted)] text-[11px]">
                        <div className="font-medium text-[var(--gv-ink)]">{cls.level}</div>
                        <div className="text-[10px] text-[var(--gv-ink-muted)]">{cls.batch}</div>
                      </td>
                      <td className="py-3 px-3.5 font-semibold text-[var(--gv-ink)]">{cls.teacherName}</td>
                      <td className="py-3 px-3.5 text-[var(--gv-ink-muted)] font-mono text-[11px]">
                        {cls.dateTime.split(' ')[1]} {cls.dateTime.split(' ')[2]}
                      </td>
                      <td className="py-3 px-3.5">
                        <Badge
                          variant={isLive ? 'soft' : 'accent'}
                          className="font-mono gap-1.5"
                        >
                          {isLive && <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-pulse" />}
                          {cls.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-3.5 text-right">
                        <Button
                          size="sm"
                          leftIcon={Play}
                          onClick={() => {
                            addToast('info', 'Class Launcher', `Joining video session for ${cls.title}`);
                            window.open(cls.meetingLink, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          {isLive ? 'Join Live' : 'Start Session'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* System Activity & Audit Stream */}
        <div className="admin-card space-y-4 p-5">
          <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-bold text-[var(--gv-ink)]">System Activity & Audit</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onNavigateTab('administration', 'audit_logs')}
            >
              All Logs
            </Button>
          </div>

          <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1">
            {auditLogs.slice(0, 5).map((log) => (
              <div
                key={log.id}
                className="p-3 bg-[var(--gv-surface)]/70 border border-[var(--gv-border)] rounded-lg space-y-1 hover:bg-stone-100/60 transition-colors"
              >
                <div className="flex items-center justify-between text-[10px] font-mono">
                  <span className="font-bold text-[var(--gv-brand)]">{log.actorName} ({log.actorRole})</span>
                  <span className="text-[var(--gv-ink-muted)]">{log.timestamp.split(' ')[1]} {log.timestamp.split(' ')[2]}</span>
                </div>
                <p className="text-xs font-medium text-[var(--gv-ink)]">{log.details}</p>
                <div className="flex items-center justify-between pt-1 text-[10px] text-[var(--gv-ink-muted)] font-mono">
                  <span>Module: {log.module}</span>
                  <Badge variant="success" className="font-mono normal-case tracking-normal">
                    ● {log.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
